/**
 * THE FILES IN A PASTE, from either place a browser puts them.
 *
 * Owner, 2026-09-19: 「幫我把這個 app 的 text editor 文字輸入框加上可以貼上照片的
 * 功能…這是要在手機的窄版面可以做到的事情」
 *
 * WHY THIS IS NOT JUST `e.clipboardData.files`. That is what the composer read
 * before, and it is enough on a desktop browser. It is NOT reliably enough for
 * the case he actually asked for — a photo copied on a phone. Safari and the
 * WKWebView shells have historically delivered a pasted image through
 * `items` (kind "file") with `files` left empty, and the two are not required to
 * agree: the spec populates `files` from the items that ARE files, but coverage
 * across the shells this app runs in (Capacitor on iOS, a bare WKWebView on the
 * Mac, Safari, Chrome) is exactly the sort of thing that works on the machine you
 * tested and not on the one he uses.
 *
 * FILES FIRST, AND ITEMS ONLY IF FILES YIELDED NOTHING — corrected 2026-09-19
 * after the owner pasted one screenshot and got two.
 *
 * The first version read BOTH and deduped on name+size+lastModified+type, which
 * looked careful and was not: `getAsFile()` SYNTHESISES a File object, stamped
 * with the moment it was called, so the same photo arrives from `files` and from
 * `items` with lastModified a millisecond apart. Different key, no match, two
 * attachments — his screenshot shows image.png 325 KB twice.
 *
 * A better key would have been another guess. The spec says `files` is derived
 * from exactly the items whose kind is "file", so when `files` has anything it is
 * already the whole answer and `items` can only repeat it. Reading items ONLY on
 * an empty `files` removes the duplicate at its source instead of detecting it
 * afterwards, and keeps the phone fallback that made this function necessary.
 *
 * The dedupe stays as a belt WITHIN one source, minus lastModified — the field
 * that caused this — so a browser that lists one file twice still attaches it
 * once, without the unstable part of the key.
 *
 * ONE PLACE FOR ALL FOUR SURFACES. He guessed this would be a single change
 * because it is a web app, and he is right: the composer is one component and the
 * phone, the iPad, the Mac shell and the browser all render it. Nothing here is
 * platform-specific — it reads a DataTransfer, which is all any of them provide.
 */

/** A paste carrying no files at all is the ordinary case — text — and yields []. */
export function filesFromClipboard(data: DataTransfer | null | undefined): File[] {
  if (!data) return []
  const out: File[] = []
  const seen = new Set<string>()
  /** Same file from `files` and from `items` must attach once, not twice. */
  const add = (f: File | null) => {
    if (!f) return
    // Name alone is a poor key — a phone names every paste "image.png" — so size
    // and type join it. lastModified is deliberately NOT here: it is synthesised
    // per call by getAsFile and was the reason one paste became two.
    const key = `${f.name}|${f.size}|${f.type}`
    if (seen.has(key)) return
    seen.add(key)
    out.push(f)
  }
  for (const f of Array.from(data.files ?? [])) add(f)
  if (out.length) return out
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind !== 'file') continue
    add(item.getAsFile())
  }
  return out
}
